import { ConfirmChangesDialog } from 'components/organisms/NavigationBlocker';
import { GeneralPopup, PageHeader, PageWrapper } from 'components/partials';
import { AutoComplete, Button, EmptyState, Icon, Table, Tabs, WarningMessage } from 'components/widgets';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { IconSizes } from '../components/foundations/Icon';
import DownloadIcon from '../resources/icons/download.svg';
import { useAsync } from '../utils/customHooks';
import { convertToCsv, download } from '../utils/export';
import WizardWarningMessage, { MessageTypes } from './components/WizardWarningMessage';
import AgencyDialog from './containers/AgencyDialog';
import ConfigurationRequiredDialog from './containers/ConfigurationRequiredDialog';
import ManagePermissionsPage from './containers/ManagePermissionsPage';
import { SECTION_PAGE_SIZE } from './reducer';
import TeamManagementAPI from './service';
import css from './styles.css';
import { getTableColumns } from './tableUtils';
import { MembersGroupsSchema } from './types';
import {
    DELETABLE_GROUPS,
    getUserDisplayName,
    getUserRole,
    isWithinReInviteCooldown,
    MEMBER_GROUP_TYPES,
    MEMBER_TYPES,
    trackTeamManagementMixpanelEvent,
} from './utils';

const api = new TeamManagementAPI();

// Placeholder rows rendered before any section has finished its first fetch.
const INITIAL_SKELETON_ROWS = 5;

const getExportTeamFileName = () => `singular_team_${new Date().toISOString()}.csv`;

// Map section sort state (asc/desc) to the column-header arrow format (down/up).
const sectionSortToConfig = section => ({
    name: section.sortBy,
    direction: section.sortDir === 'asc' ? 'down' : 'up',
});

const SectionShape = PropTypes.shape({
    items: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
    offset: PropTypes.number.isRequired,
    limit: PropTypes.number.isRequired,
    hasMore: PropTypes.bool.isRequired,
    search: PropTypes.string.isRequired,
    sortBy: PropTypes.string.isRequired,
    sortDir: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    totalPending: PropTypes.bool,
    error: PropTypes.any,
    requestId: PropTypes.number,
});

const Page = ({
    memberTypes = [],
    activeMemberType = MEMBER_TYPES.USER,
    onActiveMemberTypeChange = () => {},
    currentUserOrgId,
    adminModeEnabled = false,
    agenciesReady = false,
    membersGroups = [],
    pageLoaded = false,
    page = { show: false },
    onDeleteMember = () => {},
    onDeleteBtnClicked = () => {},
    onCreateNewMember = () => {},
    onManagePermissions = () => {},
    onResendInvitation = () => {},
    translate = () => {},
    confirmChangesDialogOpen = false,
    onConfirmChangesAccepted = () => {},
    onConfirmChangesRejected = () => {},
    savedMemberName = '',
    sections = { activeUsers: {}, inactiveUsers: {}, invites: {}, agencies: {} },
    onSectionSearchChanged = () => {},
    onSetSectionSearchValue = () => {},
    onSectionSortChanged = () => {},
    onSectionPageChange = () => {},
    onRefreshSection = () => {},
}) => {
    const [exportTeamMembersResponse, runExportTeamMembers] = useAsync();
    const [memberToDelete, setMemberToDelete] = useState(null);
    const [collapsedGroupsByType, setCollapsedGroupsByType] = useState({});

    const exportStartTime = useRef(null);

    const {
        data: exportTeamMembers,
        isLoading: isLoadingExportAll,
        error: exportTeamMembersError,
    } = exportTeamMembersResponse;

    const isAgenciesEnabled = memberTypes.some(t => t.type === MEMBER_TYPES.AGENCY);

    // Eager-fetch user sections on mount.
    useEffect(() => {
        onRefreshSection('activeUsers');
        onRefreshSection('inactiveUsers');
        onRefreshSection('invites');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch agencies when the feature flag becomes available — userData loads async so
    // isAgenciesEnabled is typically false on the first render and flips to true after the
    // user/feature flags arrive. A separate effect avoids missing that flip.
    useEffect(() => {
        if (isAgenciesEnabled) {
            onRefreshSection('agencies');
        }
    }, [isAgenciesEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    // After a member is saved the page surfaces their name in the search. Fan the search out
    // to every visible section of the current tab (e.g. both Active Users AND Pending
    // Invitations) so the saved member shows up wherever they landed instead of one section
    // showing "No results".
    useEffect(() => {
        if (savedMemberName) {
            const search = savedMemberName;
            const targets =
                activeMemberType === MEMBER_TYPES.AGENCY ? ['agencies'] : ['activeUsers', 'inactiveUsers', 'invites'];
            targets.forEach(key => {
                onSetSectionSearchValue(key, search); // keep the search input value in sync
                onSectionSearchChanged(key, search); // trigger the debounced reset+refresh
            });
        }
    }, [savedMemberName]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDeleteClicked = member => {
        onDeleteBtnClicked();
        setMemberToDelete(member);
    };

    const onDeleteApproved = () => {
        onDeleteMember(memberToDelete);
        setMemberToDelete(null);
    };

    const onDeleteRejected = () => setMemberToDelete(null);

    const toggleCollapsedGroup = sectionKey => {
        setCollapsedGroupsByType(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    };

    const onExportClick = () => runExportTeamMembers(api.exportTeamMembers());

    // Section keys visible for the current tab.
    const visibleSectionKeys =
        activeMemberType === MEMBER_TYPES.USER ? ['activeUsers', 'inactiveUsers', 'invites'] : ['agencies'];

    // Primary section drives the shared column-header sort arrows.
    const primarySectionKey = activeMemberType === MEMBER_TYPES.USER ? 'activeUsers' : 'agencies';
    const primarySection = sections[primarySectionKey] || {};
    const sortConfig = primarySection.sortBy
        ? sectionSortToConfig(primarySection)
        : { name: 'name', direction: 'down' };

    // Dispatches a sort change to all sections of the current tab.
    const onChangeSort = columnName => {
        const currentDir = primarySection.sortBy === columnName ? primarySection.sortDir : 'asc';
        const newDir = currentDir === 'asc' ? 'desc' : 'asc';
        visibleSectionKeys.forEach(key => onSectionSortChanged(key, columnName, newDir));
    };

    // Search input: value from primary section, changes fan out to all visible sections.
    const searchText = primarySection.search || '';
    const onSearchChange = value => {
        // Store the raw value (preserves the user's casing in the input). Lowercasing for the
        // BE happens at the saga's request-build step (see fetchPaginatedSection).
        visibleSectionKeys.forEach(key => {
            onSetSectionSearchValue(key, value); // updates state immediately so input stays responsive
            onSectionSearchChanged(key, value); // triggers the debounced reset+refresh saga
        });
    };

    // Cell-level skeleton is on only during the very first load (before any section has
    // completed its first fetch). During subsequent refetches (search / sort / page change)
    // we keep the previous items visible — the disabled pagination buttons + the section
    // label dropping its "(N)" count are the loading indicators. This avoids flashing
    // skeletons over rows in unrelated sections that aren't actually reloading.
    const noVisibleSectionLoaded = visibleSectionKeys.every(key => !sections[key]?.loaded);
    const tableColumns = getTableColumns({
        memberType: activeMemberType,
        sortConfig,
        onChangeSort,
        onManagePermissions,
        onDelete: handleDeleteClicked,
        onResendInvitation,
        // Cell-level skeleton flips off as soon as the first visible section has loaded;
        // per-section refetches no longer paint skeletons over already-loaded rows.
        pageLoaded: pageLoaded && !noVisibleSectionLoaded,
        translate,
        searchText,
    });

    useEffect(() => {
        if (isLoadingExportAll) {
            exportStartTime.current = performance.now();
        }

        if (exportTeamMembers) {
            const fileName = getExportTeamFileName();
            const csvData = convertToCsv(exportTeamMembers.fields, exportTeamMembers.data);
            download(fileName, csvData);

            trackTeamManagementMixpanelEvent('Export Team Button Clicked', {
                durationInMilliseconds: performance.now() - exportStartTime.current,
            });
        }
    }, [exportTeamMembersResponse]);

    // On the Agency tab, the create button is gated on /agencies/self having resolved at
    // least once AND no refetch being in flight. AgencyDialog reads state.orgAgencies
    // synchronously to detect duplicates, so we don't let the user open it against an
    // empty / stale list.
    const addAgencyBlocked = activeMemberType === MEMBER_TYPES.AGENCY && !agenciesReady;
    const addButtonDisabled = !pageLoaded || addAgencyBlocked;
    const addMemberButton = (
        <Button
            type="primary"
            level="level1"
            className={css.newMemberButton}
            textClassName={css.newMemberButtonText}
            onClick={onCreateNewMember}
            disabled={addButtonDisabled}
        >
            <Icon name="add-fill" variant={addButtonDisabled ? 'light' : 'white'} size={IconSizes.SMALL} />
            <Translate id={`STATIC.BUTTONS.${activeMemberType}_CREATE_NEW_MEMBER`} />
        </Button>
    );

    const renderInfoMessages = infoMessages =>
        infoMessages.map(infoMessage => (
            <WarningMessage
                key={infoMessage.message}
                type="success"
                className={css.infoMessage}
                showIcon
                showTypeIcon
                {...infoMessage}
            />
        ));

    // Enrich a raw backend item with client-side derived fields so it matches the
    // shape that table cells and action configs expect.
    const enrichSectionItem = (item, groupName, currentUserOrgId) => {
        if (activeMemberType === MEMBER_TYPES.USER) {
            const id = item.organization_user_id || item.username;
            const isMe = id === currentUserOrgId;
            const disabled = isWithinReInviteCooldown(item.last_re_invited_at) || !!item.disabled;
            const displayName = getUserDisplayName(item);
            return {
                ...item,
                id,
                isMe,
                name: isMe ? `${displayName} (Me)` : displayName,
                description: item.role?.description || '',
                roleText: getUserRole(item),
                deletable: DELETABLE_GROUPS.includes(groupName),
                disabled,
                groupName,
                key: `member_${id}`,
                resendDisabled: disabled,
            };
        }
        // Agency items — map BE fields onto the keys the shared table columns expect.
        const agencyId = item.id || item.agency_id;
        return {
            ...item,
            id: agencyId,
            name: item.display_name,
            imgSrc: item.company_logo,
            deletable: true,
            searchText: `${item.display_name || ''};${agencyId}`.toLowerCase(),
            groupName,
            key: `member_${agencyId}`,
        };
    };

    const buildPagination = (sectionKey, section) => ({
        offset: section.offset || 0,
        limit: section.limit || SECTION_PAGE_SIZE,
        total: section.total || 0,
        hasMore: !!section.hasMore,
        // Treat the page-level skeleton as a loading state too so the footer stays hidden
        // during the initial page load until the skeleton clears.
        isLoading: !!section.isLoading || !pageLoaded,
        onPageChange: nextOffset => onSectionPageChange(sectionKey, nextOffset),
    });

    // Build group label in the same format the old selector used.
    const groupLabel = (groupKey, memberType) =>
        translate(`STATIC.PAGES.TEAM_MEMBERS.GROUP_NAME_${groupKey.toUpperCase()}_${memberType}`);

    // Show a group only after its first fetch has landed AND it has at least one row
    // overall (i.e. unfiltered). Sections that are empty from the start stay hidden;
    // sections that go empty only because of an active search still render so the user
    // sees a "No results" row.
    const isVisibleSection = section => !!section && section.loaded && section.overallTotal > 0;

    // While a section is loading (page change, search, sort), render `limit` skeleton
    // placeholders for THAT section only. The `_isSkeleton: true` flag flips the cells'
    // isLoading on per-row inside the Table widget, so unrelated already-loaded sections
    // don't flash skeletons.
    const sectionRows = (section, groupName) => {
        if (section.isLoading) {
            return Array.from({ length: section.limit || SECTION_PAGE_SIZE }, (_, i) => ({
                key: `__skeleton__${groupName}_${i}`,
                _isSkeleton: true,
            }));
        }
        return section.items.map(m => enrichSectionItem(m, groupName, currentUserOrgId));
    };

    // Drop the trailing "(N)" only while the filtered total is being recomputed (search /
    // sort / reset). A plain page change leaves the total unchanged, so the count stays put
    // instead of flickering away behind the page's skeleton.
    const sectionLabel = (section, base) => (section.totalPending ? base : `${base} (${section.total})`);

    const agenciesEmpty =
        activeMemberType === MEMBER_TYPES.AGENCY &&
        !!sections.agencies?.loaded &&
        sections.agencies.overallTotal === 0;

    const renderAgencyEmptyState = () => (
        <EmptyState
            customIcon={<Icon name="briefcase" variant="light" interactive={false} />}
            containerClass={css.emptyStateContainer}
            textContainerClass={css.emptyStateContainer}
            headerClassName={css.emptyStateText}
            subHeaderClassName={css.emptyStateText}
            header="STATIC.PAGES.TEAM_MEMBERS.EMPTY_STATE.HEADER_AGENCY"
            headerStyle={{ fontSize: 20, fontWeight: 600 }}
            subHeader="STATIC.PAGES.TEAM_MEMBERS.EMPTY_STATE.SUBHEADER_AGENCY"
        >
            {addMemberButton}
        </EmptyState>
    );

    const buildGroups = () => {
        if (activeMemberType === MEMBER_TYPES.USER) {
            const groups = [];

            if (isVisibleSection(sections.invites)) {
                groups.push({
                    key: 'invites',
                    label: sectionLabel(sections.invites, groupLabel('pending', MEMBER_TYPES.USER)),
                    data: sectionRows(sections.invites, MEMBER_GROUP_TYPES.pending),
                    isCollapsed: !!collapsedGroupsByType.invites,
                    onToggleCollapse: () => toggleCollapsedGroup('invites'),
                    pagination: buildPagination('invites', sections.invites),
                });
            }

            if (isVisibleSection(sections.activeUsers)) {
                groups.push({
                    key: 'activeUsers',
                    label: sectionLabel(sections.activeUsers, groupLabel('active', MEMBER_TYPES.USER)),
                    data: sectionRows(sections.activeUsers, MEMBER_GROUP_TYPES.active),
                    isCollapsed: !!collapsedGroupsByType.activeUsers,
                    onToggleCollapse: () => toggleCollapsedGroup('activeUsers'),
                    pagination: buildPagination('activeUsers', sections.activeUsers),
                });
            }

            if (adminModeEnabled && isVisibleSection(sections.inactiveUsers)) {
                groups.push({
                    key: 'inactiveUsers',
                    label: sectionLabel(sections.inactiveUsers, groupLabel('inactive', MEMBER_TYPES.USER)),
                    data: sectionRows(sections.inactiveUsers, MEMBER_GROUP_TYPES.inactive),
                    isCollapsed: !!collapsedGroupsByType.inactiveUsers,
                    onToggleCollapse: () => toggleCollapsedGroup('inactiveUsers'),
                    pagination: buildPagination('inactiveUsers', sections.inactiveUsers),
                });
            }

            return groups;
        }

        // AGENCY tab — single group. When the org has no agencies at all (section loaded,
        // unfiltered total 0) render the "No Agencies Setup" CTA as a headerless empty-state
        // row inside the table.
        if (agenciesEmpty) {
            return [
                {
                    key: 'agencies_empty',
                    data: [],
                    emptyStateComponent: renderAgencyEmptyState(),
                },
            ];
        }

        if (!isVisibleSection(sections.agencies)) {
            return [];
        }

        return [
            {
                key: 'agencies',
                label: sectionLabel(sections.agencies, groupLabel('active', MEMBER_TYPES.AGENCY)),
                data: sectionRows(sections.agencies, MEMBER_GROUP_TYPES.active),
                isCollapsed: !!collapsedGroupsByType.agencies,
                onToggleCollapse: () => toggleCollapsedGroup('agencies'),
                pagination: buildPagination('agencies', sections.agencies),
            },
        ];
    };

    return (
        <PageWrapper className={css.pageWrapper} containerWrapperClass={css.containerWrapper} disablePadding>
            {page.show ? (
                <ManagePermissionsPage />
            ) : (
                <div className={css.mainContainer}>
                    <div className={css.headerContainer}>
                        <PageHeader
                            text="STATIC.MANAGE_TEAM"
                            leftComponents={[]}
                            rightComponent={[]}
                            subText={{
                                faqLink:
                                    'https://support.singular.net/hc/en-us/articles/360020835952-Team-Management-FAQ',
                            }}
                        />
                        {memberTypes?.length > 1 && (
                            <Tabs
                                containerStyle={{ marginTop: 10 }}
                                selected={activeMemberType}
                                onClick={onActiveMemberTypeChange}
                                tabs={memberTypes.map(({ type, count, countKnown }) => ({
                                    id: type,
                                    label: `STATIC.PAGES.TEAM_MEMBERS.TAB_LABEL_${type.toUpperCase()}`,
                                    labelData: {
                                        // Show the count once the section has loaded — including "(0)"
                                        // for an empty section. Blank only before the first fetch lands,
                                        // when the number is genuinely unknown.
                                        count: countKnown ? `(${count})` : '',
                                    },
                                }))}
                            />
                        )}
                        <div className={css.filterRow}>
                            <AutoComplete
                                placeholder={translate(
                                    `STATIC.PAGES.TEAM_MEMBERS.PLACEHOLDER.${activeMemberType.toUpperCase()}_SEARCH`
                                )}
                                containerStyle={{ flexBasis: '500px', marginRight: '20px' }}
                                selectOptions={{
                                    options: [],
                                    isSearchable: true,
                                    inputValue: searchText,
                                    noOptionsMessage: () => null,
                                }}
                                onInputChange={onSearchChange}
                            />
                            <div className={css.buttonsContainer}>
                                {activeMemberType === MEMBER_TYPES.USER && (
                                    <Button
                                        type="secondary"
                                        className={css.exportTeamMembersButton}
                                        showSpinner={!!isLoadingExportAll}
                                        Icon={DownloadIcon}
                                        onClick={onExportClick}
                                        iconClass={css.downloadIcon}
                                        disabled={!pageLoaded}
                                    >
                                        <Translate id="STATIC.BUTTONS.EXPORT" />
                                    </Button>
                                )}
                                {addMemberButton}
                            </div>
                        </div>
                        {exportTeamMembersError && (
                            <WizardWarningMessage
                                type={MessageTypes.ERROR}
                                show
                                duration={1000}
                                message="STATIC.PAGES.TEAM_MEMBERS.EXPORT_ERROR"
                                className={css.exportTeamMembersError}
                            />
                        )}
                    </div>
                    <div className={css.membersContainer}>
                        {renderInfoMessages(membersGroups.flatMap(g => g.infoMessages))}
                        {noVisibleSectionLoaded ? (
                            // Initial load: no section has finished its first fetch yet.
                            // Render placeholder rows via the flat path so the user sees
                            // a skeleton table instead of an empty container.
                            <Table
                                key={`${activeMemberType}_skeleton`}
                                metadata={tableColumns}
                                data={Array.from({ length: INITIAL_SKELETON_ROWS }, (_, i) => ({
                                    key: `__skeleton__${i}`,
                                }))}
                            />
                        ) : (
                            <Table key={activeMemberType} metadata={tableColumns} groups={buildGroups()} />
                        )}
                    </div>
                </div>
            )}
            <GeneralPopup
                open={!!memberToDelete}
                type="delete"
                title="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.TITLE"
                text="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.MESSAGE"
                acceptText="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.ACCEPT_BUTTON_TEXT"
                rejectText="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.REJECT_BUTTON_TEXT"
                onAccept={onDeleteApproved}
                onReject={onDeleteRejected}
            />
            <AgencyDialog />
            <ConfigurationRequiredDialog />
            <ConfirmChangesDialog
                open={confirmChangesDialogOpen}
                onAccept={onConfirmChangesAccepted}
                onReject={onConfirmChangesRejected}
            />
        </PageWrapper>
    );
};

Page.propTypes = {
    pageLoaded: PropTypes.bool,
    memberTypes: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string,
            count: PropTypes.number,
            countKnown: PropTypes.bool,
        })
    ),
    currentUserOrgId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    adminModeEnabled: PropTypes.bool,
    membersGroups: MembersGroupsSchema.isRequired,
    activeMemberType: PropTypes.string,
    onActiveMemberTypeChange: PropTypes.func,
    onDeleteMember: PropTypes.func,
    onDeleteBtnClicked: PropTypes.func,
    onManagePermissions: PropTypes.func,
    onResendInvitation: PropTypes.func,
    onCreateNewMember: PropTypes.func,
    translate: PropTypes.func,
    confirmChangesDialogOpen: PropTypes.bool,
    onConfirmChangesAccepted: PropTypes.func,
    onConfirmChangesRejected: PropTypes.func,
    savedMemberName: PropTypes.string,
    page: PropTypes.shape({
        show: PropTypes.bool,
        loading: PropTypes.bool,
        selectedTabIndex: PropTypes.number,
        stepValidationStatus: PropTypes.shape({
            isValid: PropTypes.bool,
            title: PropTypes.string,
        }),
    }),
    sections: PropTypes.shape({
        activeUsers: SectionShape,
        inactiveUsers: SectionShape,
        invites: SectionShape,
        agencies: SectionShape,
    }),
    onSectionSearchChanged: PropTypes.func,
    onSetSectionSearchValue: PropTypes.func,
    onSectionSortChanged: PropTypes.func,
    onSectionPageChange: PropTypes.func,
    onRefreshSection: PropTypes.func,
};

export default withLocalize(Page);
